import { cloneDeep } from 'lodash-es';
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User } from './types';

export function useDebounce<T>(value: T, delay = 500): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, setDebouncedValue, delay]);

  return debouncedValue;
}

export function useQuery(): URLSearchParams {
  return new URLSearchParams(useLocation().search);
}

export function useTicks(frequencyInMs = 500): number {
  const [ticks, tick] = useReducer(ticks => ticks + 1, 0);
  useEffect(() => {
    const timeout = setTimeout(tick, frequencyInMs);
    return () => clearTimeout(timeout);
  }, [ticks, frequencyInMs]);
  return ticks;
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>();
  useEffect(() => {
    ref.current = cloneDeep(value);
  }, [value]);
  return ref.current;
}

// This is similar to useStorageState from 'react-storage-hooks',
// but does not subscribe to storage changes, so that user actions
// on one tab do not immediately effect another tab (#2646)
export function useStorageState<T>(
  store: Storage,
  name: string,
  defaultState: T | (() => T)
): [T, Dispatch<SetStateAction<T>>];
export function useStorageState<T>(
  store: Storage,
  name: string
): [T | null, Dispatch<SetStateAction<T | null>>];
export function useStorageState<T>(
  store: Storage,
  name: string,
  defaultState: T | (() => T) | null = null
): [T | null, Dispatch<SetStateAction<T | null>>] {
  const initialStoreState = store.getItem(name);
  const [state, setState] = useState<T | null>(
    initialStoreState ? JSON.parse(initialStoreState) : defaultState
  );

  useEffect(() => {
    if (state === null) store.removeItem(name);
    else store.setItem(name, JSON.stringify(state));
  }, [state, name, store]);

  return [state, setState];
}

export function useUserMap(clientId?: number): Record<string, User> {
  //todo need users
  //const { data: users } = useGet<User[]>('/api/v1/users/names', { clientId });
  const users: User[] = [
    {
      email: 'todo here',
      fullName: 'todo here',
      phone_number: 'todo here',
      role: 'todo here',
      title: 'todo here'
    }
  ];
  const userMap = useMemo(
    () =>
      (users ?? []).reduce(
        (acc, user) => ({ ...acc, [user.email]: user }),
        {} as Record<string, User>
      ),
    [users]
  );
  return userMap;
}

export function useNavigationGuard(isDirty: boolean, onBlock: (nextPath: string) => void) {
  const location = useLocation();

  // Block anchor tag clicks
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement;
      if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;

      const nextPath = anchor.href.replace(window.location.origin, '');
      if (nextPath !== location.pathname && isDirty) {
        e.preventDefault();
        onBlock(nextPath);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [isDirty, location.pathname, onBlock]);

  // Block browser tab close or refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
}

export function useSafeNavigate(isDirty: boolean, onBlock: (nextPath: string) => void) {
  const navigate = useNavigate();

  return useCallback(
    (path: string) => {
      if (isDirty) {
        onBlock(path); // trigger confirmation modal
      } else {
        navigate(path);
      }
    },
    [isDirty, navigate, onBlock]
  );
}
